/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { EpisodeInfo, SeasonsData } from 'client-code/episode-guide';

import { EpisodeGuideServiceRepositoryImpl } from '../impls';
import { IEpisodeGuideService, IEpisodeGuideServiceRepository } from '../interfaces';
import {
    TEpisodeInfo,
    TEpisodeParams,
    TPlayNextInfo,
    TPlayNextParams,
    TSeasonsParams,
    TServicesParams,
} from '../types';
import { lruSet } from '../utils/LruCacheUtils';

export class EpisodeGuideService implements IEpisodeGuideService {
    private static mSeasonDataMap: Map<string, SeasonsData> = new Map();

    private static mCachedEpisodes: Map<string, Map<string, EpisodeInfo[]>> = new Map();

    private static mFilterState: Map<string, { sort: string }> = new Map();

    private oEpisodeGuideServiceRepository: IEpisodeGuideServiceRepository;

    public constructor(params: TServicesParams) {
        this.oEpisodeGuideServiceRepository = new EpisodeGuideServiceRepositoryImpl(params);
    }

    /**
     * @inheritdoc
     */
    public async getSeasonData(seasonsParams: TSeasonsParams): Promise<SeasonsData> {
        if (EpisodeGuideService.mSeasonDataMap.has(seasonsParams.filter)) {
            return EpisodeGuideService.mSeasonDataMap.get(seasonsParams.filter)!;
        }
        const seasonsResponse =
            await this.oEpisodeGuideServiceRepository.fetchSeasons(seasonsParams);
        lruSet(EpisodeGuideService.mSeasonDataMap, seasonsParams.filter, seasonsResponse, 30);
        return seasonsResponse;
    }

    /**
     * @inheritdoc
     */
    // eslint-disable-next-line class-methods-use-this
    public clearSeasonsData(): void {
        EpisodeGuideService.mSeasonDataMap.clear();
    }

    private static generateCacheKey(episodeParams: TEpisodeParams): string {
        const {
            filter,
            sort,
            offset,
            seasonOrYear,
            unifiedCollectionId,
            collapsedVodItemId,
            contentSupplierPartnerId,
            hideAdult,
            isNewV2,
            limit,
            partnerId,
        } = episodeParams;
        return JSON.stringify({
            filter,
            sort,
            offset,
            seasonOrYear,
            unifiedCollectionId,
            collapsedVodItemId,
            contentSupplierPartnerId,
            hideAdult,
            isNewV2,
            limit,
            partnerId,
        });
    }

    /**
     * @inheritdoc
     */
    public async getEpisodes(episodeParams: TEpisodeParams): Promise<TEpisodeInfo> {
        const { filter, sort, networkCallId } = episodeParams;
        const cacheKey = EpisodeGuideService.generateCacheKey(episodeParams);
        let filterCache = EpisodeGuideService.mCachedEpisodes.get(filter);
        if (!filterCache) {
            filterCache = new Map<string, EpisodeInfo[]>();
            lruSet(EpisodeGuideService.mCachedEpisodes, filter, filterCache, 20);
        }
        let filterState = EpisodeGuideService.mFilterState.get(filter);
        if (!filterState) {
            filterState = { sort: '' };
            lruSet(EpisodeGuideService.mFilterState, filter, filterState, 20);
        }
        const hasSortChanged = filterState.sort !== sort;
        if (hasSortChanged) {
            filterCache.clear();
            filterState.sort = sort || '';
        }
        const cachedData = filterCache.get(cacheKey);
        if (cachedData) {
            return { episodeInfo: cachedData, networkCallId };
        }
        const res = await this.oEpisodeGuideServiceRepository.fetchEpisodes(episodeParams);
        lruSet(filterCache, cacheKey, res.episodeInfo, 50);

        return res;
    }

    public async getPlayNext(playNextParams: TPlayNextParams): Promise<TPlayNextInfo> {
        const {
            contentId,
            unifiedCollectionId,
            filter,
            recordingId,
            sort,
            partnerId,
            contentSupplierPartnerId,
            collapsedMixId,
            collapsedVodItemId,
            isNewV2,
            networkCallId,
        } = playNextParams;
        return this.oEpisodeGuideServiceRepository.fetchPlayNext({
            contentId,
            unifiedCollectionId,
            filter,
            recordingId,
            sort,
            partnerId,
            contentSupplierPartnerId,
            collapsedMixId,
            collapsedVodItemId,
            isNewV2,
            networkCallId,
        });
    }

    // eslint-disable-next-line class-methods-use-this
    public clearCachedOffers(): void {
        EpisodeGuideService.mCachedEpisodes.clear();
        EpisodeGuideService.mFilterState.clear();
    }
}
